/**
 * /admin/run — admin console for the "Road to Michigan // The Chila Run" tracker.
 * Inline login gated by academy_token (admin/admin_role required server-side),
 * mirroring the order tracker console.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, MapPin, Trash2, Plus, Navigation, Save, ExternalLink, LogOut, Video, Upload } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const KINDS = ['start', 'bucket', 'heritage', 'destination'];
const BOY_KEYS = ['b2', 'brave'];
const emptyStop = { city: '', node: '', label: '', status: '', lat: '', lon: '', kind: 'bucket', boys: ['b2'], fit: '/chila' };

const AdminRun = () => {
  const [authed, setAuthed] = useState(!!localStorage.getItem('academy_token'));
  const [creds, setCreds] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState({ title: '', subtitle: '', destination_label: '', current_stop_id: '' });
  const [newStop, setNewStop] = useState(emptyStop);
  const [busy, setBusy] = useState(false);
  const [locQuery, setLocQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/run/tracker');
      const t = r.data.tracker;
      setCfg(t);
      setMeta({ title: t.title || '', subtitle: t.subtitle || '', destination_label: t.destination_label || '', current_stop_id: t.current_stop_id || '' });
    } catch (e) { toast.error('Failed to load tracker'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/academy/auth/login', creds);
      const token = res.data.token || res.data.access_token;
      localStorage.setItem('academy_token', token);
      setAuthed(true);
      toast.success('Signed in');
    } catch (err) { toast.error('Login failed — admin account required'); }
  };

  const logout = () => { localStorage.removeItem('academy_token'); setAuthed(false); };

  const saveMeta = async () => {
    setBusy(true);
    try { const r = await api.put('/run/tracker', meta, { headers: authHeaders() }); setCfg(r.data.tracker); toast.success('Saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  const checkIn = async (stopId) => {
    try { const r = await api.post('/run/checkin', { stop_id: stopId }, { headers: authHeaders() }); setCfg(r.data.tracker); setMeta((m) => ({ ...m, current_stop_id: stopId })); toast.success('Vehicle moved to this stop'); }
    catch (e) { toast.error('Check-in failed'); }
  };

  const editStop = async (stop, patch) => {
    const payload = { ...stop, ...patch, lat: parseFloat(patch.lat ?? stop.lat), lon: parseFloat(patch.lon ?? stop.lon) };
    try { const r = await api.patch(`/run/stops/${stop.id}`, payload, { headers: authHeaders() }); setCfg(r.data.tracker); toast.success('Stop updated'); }
    catch (e) { toast.error('Update failed'); }
  };

  const deleteStop = async (id) => {
    if (!window.confirm('Remove this stop?')) return;
    try { const r = await api.delete(`/run/stops/${id}`, { headers: authHeaders() }); setCfg(r.data.tracker); toast.success('Removed'); }
    catch (e) { toast.error('Delete failed'); }
  };

  const addStop = async () => {
    if (!newStop.city || newStop.lat === '' || newStop.lon === '') { toast.error('City, lat and lon are required'); return; }
    setBusy(true);
    try {
      const payload = { ...newStop, lat: parseFloat(newStop.lat), lon: parseFloat(newStop.lon) };
      const r = await api.post('/run/stops', payload, { headers: authHeaders() });
      setCfg(r.data.tracker); setNewStop(emptyStop); toast.success('Stop added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
    setBusy(false);
  };

  const toggleBoy = (arr, key) => (arr.includes(key) ? arr.filter((b) => b !== key) : [...arr, key]);

  const geocodeAdd = async () => {
    if (!locQuery.trim()) return;
    try {
      const r = await api.get('/run/geocode', { params: { q: locQuery } });
      setNewStop((s) => ({ ...s, city: locQuery.trim(), lat: r.data.lat.toFixed(4), lon: r.data.lon.toFixed(4) }));
      toast.success('Coordinates found');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Location not found'); }
  };

  const geocodeStop = async (stop) => {
    if (!stop.city) { toast.error('Enter a city first'); return; }
    try {
      const r = await api.get('/run/geocode', { params: { q: stop.city } });
      await editStop(stop, { lat: r.data.lat.toFixed(4), lon: r.data.lon.toFixed(4) });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Location not found'); }
  };

  const uploadVideo = async (stopId, input) => {
    const f = input.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    setBusy(true);
    try {
      const r = await api.post(`/run/stops/${stopId}/video`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      setCfg(r.data.tracker); toast.success('Video uploaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    setBusy(false);
    input.value = '';
  };

  const removeVideo = async (stopId) => {
    try { const r = await api.delete(`/run/stops/${stopId}/video`, { headers: authHeaders() }); setCfg(r.data.tracker); toast.success('Video removed'); }
    catch (e) { toast.error('Remove failed'); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0b0b0d] text-[#f4efe6] flex items-center justify-center px-5">
        <form onSubmit={login} className="w-full max-w-sm border border-white/10 bg-[#141416] p-8" data-testid="run-admin-login">
          <div className="text-[10px] tracking-[0.35em] uppercase font-black text-[#C9A961] mb-2">The Chila Run · Admin</div>
          <h1 className="text-2xl font-black uppercase tracking-tight mb-6">Command Console</h1>
          <input type="email" placeholder="Admin email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })}
            className="w-full bg-[#0b0b0d] border border-white/15 px-4 py-3 text-sm mb-3 outline-none focus:border-[#C9A961]" data-testid="run-admin-email" />
          <input type="password" placeholder="Password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })}
            className="w-full bg-[#0b0b0d] border border-white/15 px-4 py-3 text-sm mb-5 outline-none focus:border-[#C9A961]" data-testid="run-admin-password" />
          <button type="submit" className="w-full bg-[#C9A961] text-black py-3 text-xs font-black tracking-[0.25em] uppercase hover:bg-[#d8bd7f]" data-testid="run-admin-login-btn">Sign In</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0b0d] text-[#f4efe6]" data-testid="admin-run">
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase font-black text-[#C9A961]">The Chila Run · Admin</div>
            <h1 className="text-3xl font-black uppercase tracking-tight">Command Console</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/road-to-michigan" target="_blank" className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[10px] font-black tracking-[0.2em] uppercase hover:border-[#C9A961]" data-testid="run-view-public">
              View Live Map <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <button onClick={logout} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[10px] font-black tracking-[0.2em] uppercase hover:border-red-500 hover:text-red-400"><LogOut className="w-3.5 h-3.5" /> Logout</button>
          </div>
        </div>

        {loading || !cfg ? (
          <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#C9A961]" /></div>
        ) : (
          <>
            {/* Meta */}
            <section className="border border-white/10 bg-[#141416] p-5 mb-8">
              <div className="text-[10px] tracking-[0.3em] uppercase font-black text-[#C9A961] mb-4">Run Details</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Title" value={meta.title} onChange={(v) => setMeta({ ...meta, title: v })} testid="run-meta-title" />
                <Field label="Destination Label" value={meta.destination_label} onChange={(v) => setMeta({ ...meta, destination_label: v })} testid="run-meta-dest" />
                <div className="sm:col-span-2"><Field label="Subtitle" value={meta.subtitle} onChange={(v) => setMeta({ ...meta, subtitle: v })} testid="run-meta-subtitle" /></div>
              </div>
              <button onClick={saveMeta} disabled={busy} className="mt-4 inline-flex items-center gap-2 bg-[#C9A961] text-black px-5 py-2.5 text-[10px] font-black tracking-[0.2em] uppercase hover:bg-[#d8bd7f] disabled:opacity-40" data-testid="run-save-meta"><Save className="w-3.5 h-3.5" /> Save Details</button>
            </section>

            {/* Stops */}
            <section className="mb-8">
              <div className="text-[10px] tracking-[0.3em] uppercase font-black text-[#C9A961] mb-4">Route Stops · {cfg.stops?.length || 0}</div>
              <div className="space-y-3">
                {(cfg.stops || []).map((s) => {
                  const isCurrent = s.id === cfg.current_stop_id;
                  return (
                    <div key={s.id} className={`border p-4 bg-[#141416] ${isCurrent ? 'border-green-500/60' : 'border-white/10'}`} data-testid={`run-stop-${s.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#C9A961]" />
                          <span className="font-black uppercase tracking-tight">{s.city}</span>
                          <span className="text-[9px] tracking-[0.2em] uppercase text-white/40">{s.node}</span>
                          {isCurrent && <span className="text-[9px] tracking-[0.2em] uppercase font-black text-green-400 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400" /> Vehicle Here</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {!isCurrent && <button onClick={() => checkIn(s.id)} className="inline-flex items-center gap-1.5 border border-green-500/50 text-green-400 px-3 py-1.5 text-[9px] font-black tracking-[0.15em] uppercase hover:bg-green-500/10" data-testid={`run-checkin-${s.id}`}><Navigation className="w-3 h-3" /> Check In Here</button>}
                          <button onClick={() => deleteStop(s.id)} className="text-white/40 hover:text-red-400" data-testid={`run-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2 mt-3">
                        <Field label="City" value={s.city} onChange={(v) => editStop(s, { city: v })} small />
                        <Field label="Node" value={s.node} onChange={(v) => editStop(s, { node: v })} small />
                        <Field label="Label" value={s.label} onChange={(v) => editStop(s, { label: v })} small />
                        <Field label="Status" value={s.status} onChange={(v) => editStop(s, { status: v })} small />
                        <Field label="Lat" value={s.lat} onChange={(v) => editStop(s, { lat: v })} small />
                        <Field label="Lon" value={s.lon} onChange={(v) => editStop(s, { lon: v })} small />
                        <div>
                          <label className="text-[9px] tracking-[0.2em] uppercase text-white/40">Kind</label>
                          <select value={s.kind} onChange={(e) => editStop(s, { kind: e.target.value })} className="w-full bg-[#0b0b0d] border border-white/15 px-3 py-2 text-xs mt-1 outline-none focus:border-[#C9A961]">
                            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                          </select>
                        </div>
                        <Field label="Fit link" value={s.fit} onChange={(v) => editStop(s, { fit: v })} small />
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-[9px] tracking-[0.2em] uppercase text-white/40">Boys:</span>
                        {BOY_KEYS.map((b) => (
                          <button key={b} onClick={() => editStop(s, { boys: toggleBoy(s.boys || [], b) })}
                            className={`px-2.5 py-1 text-[9px] font-black tracking-[0.15em] uppercase border ${(s.boys || []).includes(b) ? 'bg-[#C9A961] text-black border-[#C9A961]' : 'border-white/20 text-white/60'}`}>
                            {(cfg.boys?.[b]?.name) || b}
                          </button>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center gap-2">
                        <button onClick={() => geocodeStop(s)} className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-1.5 text-[9px] font-black tracking-[0.15em] uppercase hover:border-[#C9A961]" data-testid={`run-locate-${s.id}`}><MapPin className="w-3 h-3" /> Locate From City</button>
                        <label className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-1.5 text-[9px] font-black tracking-[0.15em] uppercase hover:border-[#C9A961] cursor-pointer">
                          <Upload className="w-3 h-3" /> {s.video ? 'Replace Video' : 'Upload Video'}
                          <input type="file" accept="video/*" className="hidden" onChange={(e) => uploadVideo(s.id, e.target)} data-testid={`run-video-${s.id}`} />
                        </label>
                        {s.video && (
                          <span className="inline-flex items-center gap-2 text-[9px] tracking-[0.15em] uppercase font-black text-green-400">
                            <Video className="w-3 h-3" /> Video Attached
                            <button onClick={() => removeVideo(s.id)} className="text-white/40 hover:text-red-400" data-testid={`run-video-remove-${s.id}`}><Trash2 className="w-3 h-3" /></button>
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Add stop */}
            <section className="border border-[#C9A961]/40 bg-[#141416] p-5" data-testid="run-add-stop">
              <div className="text-[10px] tracking-[0.3em] uppercase font-black text-[#C9A961] mb-4">Add A Stop</div>
              <div className="flex items-end gap-2 mb-3">
                <div className="flex-1">
                  <label className="text-[9px] tracking-[0.2em] uppercase text-white/40">Location lookup (type a city, then Find)</label>
                  <input value={locQuery} onChange={(e) => setLocQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); geocodeAdd(); } }} placeholder="e.g. Kansas City, MO" className="w-full bg-[#0b0b0d] border border-white/15 px-3 py-2 text-xs mt-1 outline-none focus:border-[#C9A961]" data-testid="run-geo-input" />
                </div>
                <button onClick={geocodeAdd} className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 px-4 py-2 text-[10px] font-black tracking-[0.15em] uppercase hover:border-[#C9A961]" data-testid="run-geo-find"><MapPin className="w-3.5 h-3.5" /> Find</button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Field label="City *" value={newStop.city} onChange={(v) => setNewStop({ ...newStop, city: v })} small testid="run-new-city" />
                <Field label="Node" value={newStop.node} onChange={(v) => setNewStop({ ...newStop, node: v })} small />
                <Field label="Label" value={newStop.label} onChange={(v) => setNewStop({ ...newStop, label: v })} small />
                <Field label="Status" value={newStop.status} onChange={(v) => setNewStop({ ...newStop, status: v })} small />
                <Field label="Lat *" value={newStop.lat} onChange={(v) => setNewStop({ ...newStop, lat: v })} small testid="run-new-lat" />
                <Field label="Lon *" value={newStop.lon} onChange={(v) => setNewStop({ ...newStop, lon: v })} small testid="run-new-lon" />
                <div>
                  <label className="text-[9px] tracking-[0.2em] uppercase text-white/40">Kind</label>
                  <select value={newStop.kind} onChange={(e) => setNewStop({ ...newStop, kind: e.target.value })} className="w-full bg-[#0b0b0d] border border-white/15 px-3 py-2 text-xs mt-1 outline-none focus:border-[#C9A961]">
                    {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <Field label="Fit link" value={newStop.fit} onChange={(v) => setNewStop({ ...newStop, fit: v })} small />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[9px] tracking-[0.2em] uppercase text-white/40">Boys:</span>
                {BOY_KEYS.map((b) => (
                  <button key={b} onClick={() => setNewStop({ ...newStop, boys: toggleBoy(newStop.boys, b) })}
                    className={`px-2.5 py-1 text-[9px] font-black tracking-[0.15em] uppercase border ${newStop.boys.includes(b) ? 'bg-[#C9A961] text-black border-[#C9A961]' : 'border-white/20 text-white/60'}`}>
                    {(cfg.boys?.[b]?.name) || b}
                  </button>
                ))}
              </div>
              <button onClick={addStop} disabled={busy} className="mt-4 inline-flex items-center gap-2 bg-[#C9A961] text-black px-5 py-2.5 text-[10px] font-black tracking-[0.2em] uppercase hover:bg-[#d8bd7f] disabled:opacity-40" data-testid="run-add-btn"><Plus className="w-3.5 h-3.5" /> Add Stop</button>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, small, testid }) => {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  return (
    <div>
      <label className="text-[9px] tracking-[0.2em] uppercase text-white/40">{label}</label>
      <input
        value={local ?? ''}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { if (local !== value) onChange(local); }}
        data-testid={testid}
        className={`w-full bg-[#0b0b0d] border border-white/15 px-3 ${small ? 'py-2 text-xs' : 'py-3 text-sm'} mt-1 outline-none focus:border-[#C9A961]`}
      />
    </div>
  );
};

export default AdminRun;
